import { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { Leaf, MapPin, SpinnerGap, CheckCircle, Binoculars, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/PageHero";
import { toast } from "sonner";

export default function Observations() {
  const [cands, setCands] = useState(null);
  const [obs, setObs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);

  const load = () => {
    api.get("/observations/candidates").then((r) => setCands(r.data.candidates)).catch(() => setCands([]));
    api.get("/observations").then((r) => setObs(r.data.observations)).catch(() => setObs([]));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const logMany = async (ids) => {
    if (!ids.length) { toast.error("Select some photos first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/observations/bulk", { media_ids: ids, privacy: "public" });
      toast.success(`Logged ${data.created} observation(s)`);
      setSel([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log observations"); }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHero
        seed="observations" icon={Binoculars}
        eyebrow="NaturePulse"
        title="Nature Observations"
        subtitle="Turn your nature photos into structured observations (species, date, location) that power NaturePulse. Select photos and log them in one tap."
      />

      {/* Candidates */}
      <div className="mt-6 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Ready to log {cands ? `(${cands.length})` : ""}</h2>
        {cands && cands.length > 0 && (
          <div className="flex gap-2">
            <Button data-testid="log-selected" variant="outline" onClick={() => logMany(sel)} disabled={busy || !sel.length} className="h-9 rounded-md">
              Log selected ({sel.length})
            </Button>
            <Button data-testid="log-all" onClick={() => logMany(cands.map((c) => c.id))} disabled={busy} className="h-9 gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700">
              {busy ? <SpinnerGap size={15} className="animate-spin" /> : <Plus size={15} weight="bold" />} Log all
            </Button>
          </div>
        )}
      </div>

      {cands === null ? (
        <div className="mt-8 grid place-items-center"><SpinnerGap size={26} className="animate-spin text-emerald-600" /></div>
      ) : cands.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-8 text-center">
          <Leaf size={36} weight="thin" className="mx-auto text-zinc-300" />
          <p className="mt-2 font-display font-bold">All caught up</p>
          <p className="text-sm text-muted-foreground">Every nature photo has an observation. Upload more to keep logging.</p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
          {cands.map((c) => {
            const on = sel.includes(c.id);
            return (
              <button key={c.id} type="button" data-testid={`cand-${c.id}`} onClick={() => toggle(c.id)}
                className={`group relative overflow-hidden rounded-lg border text-left transition-transform hover:-translate-y-0.5 ${on ? "border-emerald-500 ring-2 ring-emerald-500" : "border-border"}`}>
                <img src={imageUrl(c.id, "display")} alt="" loading="lazy" className="aspect-square w-full object-cover bg-zinc-100" />
                {on && <CheckCircle size={22} weight="fill" className="absolute right-1.5 top-1.5 text-emerald-500" />}
                <div className="p-2">
                  <p className="line-clamp-1 text-xs font-semibold">{c.species || c.title || "Nature"}</p>
                  <p className="line-clamp-1 text-[10px] text-muted-foreground">{[c.location?.city, c.location?.country].filter(Boolean).join(", ")}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Logged */}
      <h2 className="mt-10 font-display text-lg font-bold">Logged observations {obs ? `(${obs.length})` : ""}</h2>
      {obs && obs.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
          {obs.map((o) => (
            <Link key={o.id} to={`/photos/${o.media_id}`} data-testid={`obs-${o.id}`} className="overflow-hidden rounded-lg border border-border transition-transform hover:-translate-y-0.5">
              {o.image_url && <img src={imageUrl(o.media_id, "display")} alt="" loading="lazy" className="aspect-square w-full object-cover bg-zinc-100" />}
              <div className="p-2">
                <p className="line-clamp-1 text-xs font-semibold">{o.species?.common_name || o.category}</p>
                <p className="flex items-center gap-1 text-[10px] text-muted-foreground"><MapPin size={9} weight="fill" />{[o.location?.city, o.location?.country].filter(Boolean).join(", ")}</p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">No observations logged yet.</p>
      )}
    </div>
  );
}
